import { useUuiContext, UuiContexts } from '@epam/uui-core'
import L, { LeafletMouseEvent } from 'leaflet'
import { useEffect, useState } from 'react'
import { Marker, Popup, useMapEvents } from 'react-leaflet'
import { QUERIES } from '../../../../constants/routes'
import { useActions } from '../../../../hooks/redux/actions'
import { useAppSelector } from '../../../../hooks/redux/redux'
import { useInitialQuery } from '../../../../hooks/useInitialQuery'
import { useSideBarControlls } from '../../../../hooks/useSideBarControlls'
import { useGetPointsMutation, useGetEventsMutation } from '../../../../store'
import { getMyLocationIcon } from '../../../../utils/markers/getMarkerIcon'
import { showErrorNotification } from '../../../../utils/notifications/showErrorNotification'
import { PopupText } from './../PopupText/PopupText'

export const MapEventHandlers = () => {
  const [shouldCenteredMapToStoredItem, setShouldCenteredMapToStoredItem] =
    useState(true)
  const [position, setPosition] = useState<L.LatLng | null>(null)
  const { selectedPoint } = useAppSelector((state) => state.points)
  const { selectedEvent } = useAppSelector((state) => state.events)
  const { selectedLocation } = useAppSelector((state) => state.locations)
  const { shouldLocateUser } = useAppSelector((state) => state.app)
  const { setShouldLocateUser } = useActions()

  const [fetchPoints] = useGetPointsMutation()
  const [fetchEvents] = useGetEventsMutation()
  const { setCurrentPoints, setCurrentEvents } = useActions()

  const svc = useUuiContext<UuiContexts>()
  const initialQuery = useInitialQuery()
  const { showPointInfo, showLocationInfo } = useSideBarControlls()

  const map = useMapEvents({
    locationfound: (event) => {
      setPosition(event.latlng)
      map.flyTo(event.latlng, map.getZoom())
    },
    moveend: () => {
      const bounds = map.getBounds()
      const nw = bounds.getNorthWest()
      const se = bounds.getSouthEast()

      fetchPoints({
        nwLat: nw.lat,
        nwLng: nw.lng,
        seLat: se.lat,
        seLng: se.lng,
      })
        .unwrap()
        .then((value) => {
          if (value?.length > 0) {
            if (
              selectedPoint &&
              !value.some((point) => point.id === selectedPoint.id)
            ) {
              setCurrentPoints([...value, selectedPoint])
            } else {
              setCurrentPoints(value)
            }
          }
        })

      fetchEvents({
        nwLat: nw.lat,
        nwLng: nw.lng,
        seLat: se.lat,
        seLng: se.lng,
      })
        .unwrap()
        .then((value) => {
          if (value?.length > 0) {
            if (
              selectedEvent &&
              !value.some((event) => event.id === selectedEvent.id)
            ) {
              setCurrentEvents([...value, selectedEvent])
            } else {
              setCurrentEvents(value)
            }
          }
        })
    },
    locationerror: () => {
      const errorMsg =
        'You have refused permission to determine your location. Please, allow the browser to receive your location'
      showErrorNotification({ svc, message: errorMsg })
    },
  })

  useEffect(() => {
    if (shouldLocateUser) {
      map.locate().on('locationfound', function (e) {
        setPosition(e.latlng)
        map.flyTo(e.latlng, map.getZoom())
      })
      setShouldLocateUser(false)
    }
  }, [map, shouldLocateUser])

  useEffect(() => {
    const pointId = initialQuery.get(QUERIES.POINT_ID)
    const locationId = initialQuery.get(QUERIES.LOCATION_ID)

    const someItemIdStoredInUrl = locationId || pointId

    if (shouldCenteredMapToStoredItem && !someItemIdStoredInUrl) {
      setShouldCenteredMapToStoredItem(false)
    }

    if (shouldCenteredMapToStoredItem && someItemIdStoredInUrl) {
      if (selectedPoint) {
        showPointInfo(selectedPoint)
        map.flyTo([selectedPoint.lat, selectedPoint.lng], map.getZoom())
        setShouldCenteredMapToStoredItem(false)
      }
      if (selectedLocation) {
        showLocationInfo(selectedLocation)
        if (selectedLocation.points?.length > 0) {
          map.fitBounds(selectedLocation.points)
        }
        setShouldCenteredMapToStoredItem(false)
      }
    }
  }, [
    map,
    selectedPoint,
    selectedLocation,
    initialQuery,
    shouldCenteredMapToStoredItem,
    showLocationInfo,
    showPointInfo,
  ])

  return position === null ? null : (
    <Marker
      position={position}
      icon={getMyLocationIcon()}
      eventHandlers={{
        mouseover: (event: LeafletMouseEvent) => event.target.openPopup(),
        mouseout: (event: LeafletMouseEvent) => event.target.closePopup(),
      }}
    >
      <Popup closeButton={false}>
        <PopupText>You are here</PopupText>
      </Popup>
    </Marker>
  )
}
